import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {createSelector} from "reselect";

import {geoUtils} from '../utils/geo-utils';

// Fips mapping to pull abbreviations for display
const stateNameMap = {
  "1": {n: "Alabama", a: "AL"},
  "2": {n: "Alaska", a: "AK"},
  "4": {n: "Arizona", a: "AZ"},
  "5": {n: "Arkansas", a: "AR"},
  "6": {n: "California", a: "CA"},
  "8": {n: "Colorado", a: "CO"},
  "9": {n: "Connecticut", a: "CT"},
  "10": {n: "Delaware", a: "DE"},
  "11": {n: "District of Columbia", a: "DC"},
  "12": {n: "Florida", a: "FL"},
  "13": {n: "Georgia", a: "GA"},
  "15": {n: "Hawaii", a: "HI"},
  "16": {n: "Idaho", a: "ID"},
  "17": {n: "Illinois", a: "IL"},
  "18": {n: "Indiana", a: "IN"},
  "19": {n: "Iowa", a: "IA"},
  "20": {n: "Kansas", a: "KS"},
  "21": {n: "Kentucky", a: "KY"},
  "22": {n: "Louisiana", a: "LA"},
  "23": {n: "Maine", a: "ME"},
  "24": {n: "Maryland", a: "MD"},
  "25": {n: "Massachusetts", a: "MA"},
  "26": {n: "Michigan", a: "MI"},
  "27": {n: "Minnesota", a: "MN"},
  "28": {n: "Mississippi", a: "MS"},
  "29": {n: "Missouri", a: "MO"},
  "30": {n: "Montana", a: "MT"},
  "31": {n: "Nebraska", a: "NE"},
  "32": {n: "Nevada", a: "NV"},
  "33": {n: "New Hampshire", a: "NH"},
  "34": {n: "New Jersey", a: "NJ"},
  "35": {n: "New Mexico", a: "NM"},
  "36": {n: "New York", a: "NY"},
  "37": {n: "North Carolina", a: "NC"},
  "38": {n: "North Dakota", a: "ND"},
  "39": {n: "Ohio", a: "OH"},
  "40": {n: "Oklahoma", a: "OK"},
  "41": {n: "Oregon", a: "OR"},
  "42": {n: "Pennsylvania", a: "PA"},
  "44": {n: "Rhode Island", a: "RI"},
  "45": {n: "South Carolina", a: "SC"},
  "46": {n: "South Dakota", a: "SD"},
  "47": {n: "Tennessee", a: "TN"},
  "48": {n: "Texas", a: "TX"},
  "49": {n: "Utah", a: "UT"},
  "50": {n: "Vermont", a: "VT"},
  "51": {n: "Virginia", a: "VA"},
  "53": {n: "Washington", a: "WA"},
  "54": {n: "West Virginia", a: "WV"},
  "55": {n: "Wisconsin", a: "WI"},
  "56": {n: "Wyoming", a: "WY"},
  "72": {n: "Puerto Rico", a: "PR"},
  "78": {n: "Virgin Islands", a: "VI"}
};

// Abbreviations to NOT show due to map shape size.
const smallStateAbbr = ['VT', 'NH', 'DC', 'DE', 'RI', 'PR', 'VI', 'MD', 'NJ', 'CT', 'MA']

/**
 * Component for drawing state abbreviation labels on the States map.
 *
 * @property height - height of the parent svg in pixels for placement
 * @property width - width of the parent svg in pixels for placement
 * @property stateFips - numeric state fips value to retrieve city values from topo-utils
 *
 * @example
 * return (
 *   <UsaStateLabels
 *     width={500}
 *     height={500}
 *   />
 * )
 */
export default class UsaStateLabels extends PureComponent {

  static propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    data: PropTypes.array,
    dict: PropTypes.object,
    dimension: PropTypes.string
  }

  getWidth = (props) => props.width
  getHeight = (props) => props.height
  getData = (props) => props.data
  getDimension = (props) => props.dimension

  getPaths = createSelector(
    [this.getWidth, this.getHeight],
    (width, height) => geoUtils.getAlbersUsaStatePaths(width, height)
  )

  getScale = createSelector(
    [this.getData, this.getDimension],
    (data, dimension) => geoUtils.createQuartileFillScale(data, dimension)
  )

  getTextFill = createSelector(
    [this.getScale],
    (scale) => (value) => scale(value) === "#d7311f" ? "#ffffff" : "#000000"
  )

  getTextStroke = createSelector(
    [this.getScale],
    (scale) => (value) => scale(value) === "url(#crosshatch)"
  )

  /**
   * Gets the state name, and hides the state name on the smaller states
   */
  getStateAbbrText = state => {
    const abbr = _.get(stateNameMap, [state.id, 'a'], '');
    const shouldHideSmallState = _.includes(smallStateAbbr, abbr);

    return shouldHideSmallState ? '' : abbr;
  }

  /**
   * Gets the center of each state to position the text
   */
  getCenter = (statePath) => {
    const x = _.get(statePath, 'cent[0]', 0);
    const y = _.get(statePath, 'cent[1]', 0) + 4;

    return `translate(${x},${y})`;
  }

  render() {
    const paths = this.getPaths(this.props);
    const textFill = this.getTextFill(this.props);
    const shouldStrokeText = this.getTextStroke(this.props);

    const getValue = d => _.get(this.props.dict, [d.id], {});

    return (
      <g className="UsaStateLabels">
        {paths.map(d => (

          <g key={d.id}
             transform={this.getCenter(d)}
             textAnchor="middle"
             pointerEvents="none">

            {shouldStrokeText(getValue(d)) && (
              <text
                fill={'#fff'}
                stroke={'#fff'}
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
                fontFamily="arial,sans-serif"
                fontSize={10}
                fontWeight={600}
                children={this.getStateAbbrText(d)}
              />
            )}

            <text
              fill={textFill(getValue(d))}
              stroke="none"
              fontFamily="arial,sans-serif"
              fontSize={10}
              fontWeight={600}
              children={this.getStateAbbrText(d)}
            />

          </g>

        ))}
      </g>
    )
  }
}
